import { useEffect, useMemo, useState } from 'react';
import { LocateFixed, MapPinned, Search } from 'lucide-react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface BranchLocationPickerProps {
  address: string;
  latitude: string;
  longitude: string;
  onChange: (value: { address?: string; latitude?: string; longitude?: string }) => void;
  className?: string;
}

interface GeocodeResult {
  lat: string;
  lon: string;
  display_name: string;
}

const DEFAULT_CENTER: [number, number] = [-1.2921, 36.8219];

function parseCoordinate(value: string): number | null {
  if (!value.trim()) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function MapViewport({ center }: { center: [number, number] }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
    const timeoutId = window.setTimeout(() => {
      map.invalidateSize();
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [center, map]);

  return null;
}

function PinSelector({
  position,
  onChange,
}: {
  position: [number, number];
  onChange: (coords: [number, number]) => void;
}) {
  useMapEvents({
    click(event) {
      onChange([event.latlng.lat, event.latlng.lng]);
    },
  });

  return (
    <Marker
      draggable
      position={position}
      eventHandlers={{
        dragend(event) {
          const marker = event.target;
          const next = marker.getLatLng();
          onChange([next.lat, next.lng]);
        },
      }}
    />
  );
}

async function searchAddress(query: string): Promise<GeocodeResult | null> {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=ke&q=${encodeURIComponent(query)}`
  );

  if (!response.ok) {
    throw new Error('Unable to search this location right now.');
  }

  const results = (await response.json()) as GeocodeResult[];
  return results[0] || null;
}

export function BranchLocationPicker({
  address,
  latitude,
  longitude,
  onChange,
  className,
}: BranchLocationPickerProps) {
  const [query, setQuery] = useState(address);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setQuery(address);
  }, [address]);

  const selectedPosition = useMemo<[number, number]>(() => {
    const parsedLat = parseCoordinate(latitude);
    const parsedLng = parseCoordinate(longitude);
    if (parsedLat === null || parsedLng === null) {
      return DEFAULT_CENTER;
    }

    return [parsedLat, parsedLng];
  }, [latitude, longitude]);

  const hasSelectedPoint = parseCoordinate(latitude) !== null && parseCoordinate(longitude) !== null;

  const handleCoordinateChange = (coords: [number, number]) => {
    setError(null);
    onChange({
      latitude: coords[0].toFixed(6),
      longitude: coords[1].toFixed(6),
    });
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Enter an address or landmark to search.');
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      const result = await searchAddress(query.trim());

      if (!result) {
        setError('No matching place found. Try a nearby landmark or drop the pin manually.');
        return;
      }

      onChange({
        address: result.display_name,
        latitude: Number.parseFloat(result.lat).toFixed(6),
        longitude: Number.parseFloat(result.lon).toFixed(6),
      });
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : 'Failed to search location.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('This browser does not support location access.');
      return;
    }

    setIsLocating(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        onChange({
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        });
        setIsLocating(false);
      },
      () => {
        setError('We could not get your current location. You can still search or place the pin manually.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className={cn('space-y-4 rounded-2xl border border-brand-100 bg-brand-50/50 p-4', className)}>
      <div className="space-y-2">
        <Label htmlFor="branchLocationSearch">Address or landmark</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              id="branchLocationSearch"
              placeholder="Search a branch location or nearby landmark"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                onChange({ address: event.target.value });
              }}
              className="pl-9"
            />
          </div>
          <Button type="button" variant="outline" onClick={handleSearch} isLoading={isSearching}>
            <Search className="h-4 w-4" />
            Find on map
          </Button>
          <Button type="button" variant="ghost" onClick={handleUseCurrentLocation} isLoading={isLocating}>
            <LocateFixed className="h-4 w-4" />
            Use current
          </Button>
        </div>
        <p className="text-xs text-brand-500">
          Search first, then fine-tune by tapping the map or dragging the pin.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-brand-100">
        <MapContainer
          center={selectedPosition}
          zoom={hasSelectedPoint ? 15 : 12}
          scrollWheelZoom={false}
          style={{ height: '320px', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewport center={selectedPosition} />
          <PinSelector position={selectedPosition} onChange={handleCoordinateChange} />
        </MapContainer>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="branchLatitude">Latitude</Label>
          <div className="relative">
            <MapPinned className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input id="branchLatitude" value={latitude} readOnly className="pl-9" placeholder="Drop a pin to set" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="branchLongitude">Longitude</Label>
          <div className="relative">
            <MapPinned className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input id="branchLongitude" value={longitude} readOnly className="pl-9" placeholder="Drop a pin to set" />
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-error-600">{error}</p>}
    </div>
  );
}

export default BranchLocationPicker;
